import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import AuthLayout from "@/components/AuthLayout";

const ROLES = [
  { value: "staff", label: "Staff" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "staff",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.email && !form.phone) {
      setError("Provide either an email or a phone number.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        password: form.password,
        role: form.role,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      await register(payload);
      toast.success("Account created.");
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      eyebrow="Create Account"
      title="Set up your workspace."
      subtitle="Register with an email, a phone number, or both — you can sign in with either."
    >
      <form
        data-testid="register-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-5"
        noValidate
      >
        <div>
          <label className="block text-sm font-medium text-slate-900 mb-2" htmlFor="name">
            Full name
          </label>
          <input
            data-testid="register-name-input"
            id="name"
            required
            value={form.name}
            onChange={upd("name")}
            placeholder="Jane Doe"
            className="w-full h-12 px-4 rounded-sm bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-900 mb-2" htmlFor="email">
            Email <span className="text-slate-400 font-normal">(optional if phone is provided)</span>
          </label>
          <input
            data-testid="register-email-input"
            id="email"
            type="email"
            value={form.email}
            onChange={upd("email")}
            placeholder="you@company.com"
            className="w-full h-12 px-4 rounded-sm bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-900 mb-2" htmlFor="phone">
            Phone <span className="text-slate-400 font-normal">(optional if email is provided)</span>
          </label>
          <input
            data-testid="register-phone-input"
            id="phone"
            type="tel"
            value={form.phone}
            onChange={upd("phone")}
            placeholder="+14155550101"
            className="w-full h-12 px-4 rounded-sm bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-900 mb-2" htmlFor="password">
            Password
          </label>
          <input
            data-testid="register-password-input"
            id="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={form.password}
            onChange={upd("password")}
            placeholder="At least 8 characters"
            className="w-full h-12 px-4 rounded-sm bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-900 mb-2" htmlFor="role">
            Role
          </label>
          <select
            data-testid="register-role-select"
            id="role"
            value={form.role}
            onChange={upd("role")}
            className="w-full h-12 px-4 rounded-sm bg-slate-50 border border-slate-200 text-slate-900 focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] transition-colors"
          >
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div
            data-testid="register-error"
            role="alert"
            className="text-sm text-[#D92D20] border border-[#D92D20]/30 bg-[#D92D20]/5 px-3 py-2 rounded-sm"
          >
            {error}
          </div>
        )}

        <button
          data-testid="register-submit-button"
          type="submit"
          disabled={submitting}
          className="h-12 w-full bg-[#002FA7] text-white font-medium rounded-sm hover:bg-[#002280] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? "Creating account…" : "Create account"}
        </button>

        <p className="text-sm text-slate-600">
          Already have an account?{" "}
          <Link
            data-testid="register-goto-login-link"
            to="/login"
            className="text-[#002FA7] font-medium hover:underline"
          >
            Sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
